/**
 * Was der Monteur beim Abschluss sieht — `07` §7.8, `REPORT-001`.
 *
 * Der Abschnitt „Abschluss" listet neun Punkte, und der vorletzte lautet:
 * „‚Einsatz abschließen' mit **sichtbarer Zusammenfassung**". Genau darum geht
 * es hier. Was der Monteur gleich abschickt, steht vorher auf dem Bildschirm —
 * und zwar als Zahlen, nicht als Beruhigung.
 *
 * **Eine Null ist die wichtigste Zeile.** „Zeitposten: 0" nach vier Stunden
 * Arbeit ist der Fall, für den diese Ansicht existiert; „vollständig erfasst"
 * wäre eine Behauptung, die niemand geprüft hat. Deshalb werden leere Posten
 * ausgezeichnet statt weggelassen.
 *
 * **Der Bericht ist ein Entwurf, und das steht dabei.** Freigabe und externe
 * Veröffentlichung erfolgen nach `07` §7.8 erst nach Büroprüfung. Ein Monteur,
 * der glaubt, mit dem Senden sei die Sache beim Kunden, ruft nicht an, wenn es
 * eilig ist.
 */

import type { Einsatz } from "@hwc/demo-data";
import { zeitenZuEinsatz } from "@hwc/demo-data";
import { formatResourceRef } from "@hwc/contracts";

import { useMonteur } from "../../src/Sitzung.js";
import { zusammenfassung } from "./Berichtsentwurf.js";

export function Abschlussansicht({ einsatz }: { einsatz: Einsatz }) {
  const { bestand, auflage, zustandVon, entwurfZu } = useMonteur();

  const zustand = zustandVon(einsatz);
  // Nur im Abschluss und danach: Vorher wäre es eine Vorschau auf etwas, das
  // noch entsteht, und sie stünde jeden Tag im Weg.
  if (zustand !== "abschluss" && zustand !== "uebermittelt") return null;

  const eigene = auflage(einsatz);
  const entwurf = entwurfZu(einsatz);

  const gebuchteZeiten = zeitenZuEinsatz(bestand, einsatz.ref).length;
  const gebuchtesMaterial = bestand.material.filter(
    (m) => formatResourceRef(m.einsatz) === formatResourceRef(einsatz.ref),
  ).length;

  const zeilen = zusammenfassung(einsatz, eigene, gebuchteZeiten, gebuchtesMaterial);
  const luecken = zeilen.filter((z) => z.leer);

  return (
    <>
      <h2>Servicebericht</h2>

      <ul className="postenliste">
        {zeilen.map((z) => (
          <li key={z.was} className={z.leer ? "postenliste__fern" : ""}>
            <span>{z.was}</span>
            <span>{z.wert}</span>
          </li>
        ))}
      </ul>

      {luecken.length > 0 && (
        <p className="marke marke--warten">
          {/*
            Kein Riegel, sondern ein Hinweis. `07` §7.8 lässt eine begründete
            Ausnahme zu, und wer vor Ort steht, weiß besser als diese Ansicht,
            ob eine Null hier richtig ist. Was das Büro sehen muss, steht
            ohnehin in der internen Notiz des Entwurfs.
          */}
          {luecken.length === 1 ? "Ein Punkt ist offen" : `${luecken.length} Punkte sind offen`}:{" "}
          {luecken.map((z) => z.was).join(", ")}. Wenn das so gewollt ist, gehört der
          Grund dazu — bei einem Pflichtschritt an den Schritt in der Checkliste,
          sonst in die Tätigkeitsnotiz. Das Büro liest beides beim Prüfen.
        </p>
      )}

      {entwurf ? (
        <>
          <p className="leise">
            {entwurf.zustand === "entwurf"
              ? `Entwurf Version ${entwurf.version} ist erzeugt. Er geht mit dem `
                + "Übermitteln ans Büro."
              : `Version ${entwurf.version} liegt beim Büro zur Prüfung.`}
          </p>

          <h3>Was der Kunde später liest</h3>
          <pre className="wissenstext">{entwurf.taetigkeit}</pre>

          {entwurf.interneNotiz && (
            <>
              <h3>Nur fürs Büro</h3>
              {/*
                `07` §7.16 trennt Kundensicht und interne Notizen, „damit
                interne Chats oder nicht freigegebene Medien nicht
                versehentlich im PDF erscheinen". Die Überschrift sagt das —
                sonst schreibt jemand hier hinein, was er dem Kunden mitteilen
                wollte.
              */}
              <pre className="wissenstext">{entwurf.interneNotiz}</pre>
            </>
          )}

          <p className="leise fussnote">
            <strong>Freigabe und Veröffentlichung entscheidet das Büro</strong>
            (`07` §7.8). Mit dem Senden ist der Bericht dort — beim Kunden ist er
            damit noch nicht.
          </p>
        </>
      ) : (
        <p className="leise">
          Der Entwurf entsteht mit „Arbeit fertig" und sammelt ein, was am Einsatz
          erfasst ist.
        </p>
      )}
    </>
  );
}
